/**
 * Generic Export Dialog — column picker, date range, row selection, CSV download.
 *
 * Opens from the toolbar on any StandardListPage. Users select row scope,
 * date range, columns, and format, then click Export to download.
 *
 * Templates let users pick a preset column set instead of manually
 * selecting from 100+ columns. Default templates are hardcoded per doctype;
 * users can save their own selections as custom templates.
 *
 * Plan 186, Epics 01–02
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DatePicker } from "@/components/ui/date-picker";
import { Download, Loader2, X, Search, Bookmark, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFilterStore } from "@/lib/store/filterStore";
import { mapFiltersToFrappe } from "@/lib/erp/filterMapper";
import { useExportList } from "@/features/common/hooks/useExportList";
import type { ListSettingsFieldConfig } from "@/lib/hooks/listSettingsTypes";
import {
  DEFAULT_EXPORT_TEMPLATES,
  useExportTemplateStore,
  type ExportTemplate,
} from "@/lib/store/exportTemplateStore";

// --- Types ---

type DatePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "this_month"
  | "all_time"
  | "custom";

type ExportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  doctype: string;
  availableFields: ListSettingsFieldConfig[];
  visibleFieldOrder: string[];
  /**
   * Synthetic export-only columns (e.g. computed Payment Status). Merged into
   * the picker + template validation but NOT part of the on-screen list, so
   * they can never leak into the list's field fetch. Pre-selected by default.
   */
  extraFields?: ListSettingsFieldConfig[];
  filterStoreKey: string;
  selectedRowNames?: string[];
};

// --- Date helpers ---

const DATE_PRESETS: { value: Exclude<DatePreset, "custom">; label: string }[] =
  [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7", label: "Last 7 days" },
    { value: "this_month", label: "This month" },
    { value: "all_time", label: "All time" },
  ];

function getDateRange(
  preset: Exclude<DatePreset, "custom">,
): { from?: string; to?: string } {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  switch (preset) {
    case "today":
      return { from: fmt(today), to: fmt(today) };
    case "yesterday": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: fmt(y), to: fmt(y) };
    }
    case "last7": {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { from: fmt(d), to: fmt(today) };
    }
    case "this_month": {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(first), to: fmt(today) };
    }
    case "all_time":
      return {};
  }
}

// --- Component ---

export function ExportDialog({
  open,
  onOpenChange,
  doctype,
  availableFields,
  visibleFieldOrder,
  extraFields,
  filterStoreKey,
  selectedRowNames,
}: ExportDialogProps) {
  const { exportList, isExporting } = useExportList();
  const autoCloseRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Read current filters from store
  const filters = useFilterStore(
    useCallback((s) => s.scopedFilters[filterStoreKey], [filterStoreKey]),
  );

  // Export-only synthetic columns are pre-selected so they appear in the file
  // without the user having to hunt for them (they are off-screen otherwise).
  const extraFieldnames = useMemo(
    () => (extraFields ?? []).map((f) => f.fieldname),
    [extraFields],
  );
  const defaultSelected = useMemo(
    () => [...visibleFieldOrder, ...extraFieldnames],
    [visibleFieldOrder, extraFieldnames],
  );

  // --- Column selection state ---
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    () => new Set(defaultSelected),
  );

  // --- Row scope state ---
  const hasSelection = (selectedRowNames?.length ?? 0) > 0;
  const [rowScope, setRowScope] = useState<"all" | "selected">(
    hasSelection ? "selected" : "all",
  );

  // --- Format state ---
  const [fileFormat, setFileFormat] = useState<"csv" | "xlsx">("csv");

  // --- Date range state ---
  const [datePreset, setDatePreset] = useState<DatePreset>("all_time");
  const [customDateFrom, setCustomDateFrom] = useState<string>("");
  const [customDateTo, setCustomDateTo] = useState<string>("");

  // --- Column search state ---
  const [columnSearch, setColumnSearch] = useState("");

  // --- Column checklist logic (before template deps) ---
  // Synthetic export-only fields (e.g. computed Payment Status) are appended so
  // they are selectable and pass template validation, without ever touching the
  // on-screen list's available fields.
  const exportableFields = useMemo(
    () => [
      ...availableFields.filter((f) => !f.fieldname.startsWith("__")),
      ...(extraFields ?? []),
    ],
    [availableFields, extraFields],
  );

  // --- Template state ---
  const userTemplates = useExportTemplateStore((s) => s.userTemplates);
  const saveTemplate = useExportTemplateStore((s) => s.saveTemplate);
  const deleteTemplate = useExportTemplateStore((s) => s.deleteTemplate);
  const [saveTemplateName, setSaveTemplateName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);

  const allTemplates = useMemo(() => {
    const defaults = DEFAULT_EXPORT_TEMPLATES.filter(
      (t) => t.doctype === doctype,
    );
    const user = userTemplates.filter((t) => t.doctype === doctype);
    return [...defaults, ...user];
  }, [doctype, userTemplates]);

  const applyTemplate = useCallback(
    (template: ExportTemplate) => {
      const available = new Set(exportableFields.map((f) => f.fieldname));
      const validFields = template.fields.filter((f) => available.has(f));
      if (validFields.length > 0) {
        setSelectedFields(new Set(validFields));
      }
    },
    [exportableFields],
  );

  const handleSaveTemplate = useCallback(() => {
    const name = saveTemplateName.trim();
    if (!name || selectedFields.size === 0) return;
    saveTemplate({
      name,
      doctype,
      fields: Array.from(selectedFields),
    });
    setSaveTemplateName("");
    setShowSaveInput(false);
  }, [saveTemplateName, selectedFields, doctype, saveTemplate]);

  // Reset all state when dialog opens
  // NOTE: Do NOT include hasSelection/selectedRowNames in deps — it would
  // re-reset state while the dialog is open if the user toggles rows.
  useEffect(() => {
    if (open) {
      setSelectedFields(new Set(defaultSelected));
      setRowScope(
        (selectedRowNames?.length ?? 0) > 0 ? "selected" : "all",
      );
      setFileFormat("csv");
      setDatePreset("all_time");
      setCustomDateFrom("");
      setCustomDateTo("");
      setColumnSearch("");
      setShowSaveInput(false);
      setSaveTemplateName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultSelected]);

  // Cleanup auto-close timer on unmount
  useEffect(() => {
    return () => {
      if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    };
  }, []);

  const filteredFields = useMemo(() => {
    if (!columnSearch.trim()) return exportableFields;
    const q = columnSearch.toLowerCase();
    return exportableFields.filter(
      (f) =>
        f.label.toLowerCase().includes(q) ||
        f.fieldname.toLowerCase().includes(q),
    );
  }, [exportableFields, columnSearch]);

  const allSelected = exportableFields.every((f) =>
    selectedFields.has(f.fieldname),
  );
  const noneSelected = selectedFields.size === 0;

  const toggleField = useCallback((fieldname: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldname)) {
        next.delete(fieldname);
      } else {
        next.add(fieldname);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedFields(new Set());
    } else {
      setSelectedFields(new Set(exportableFields.map((f) => f.fieldname)));
    }
  }, [allSelected, exportableFields]);

  // --- Export handler ---
  const handleExport = useCallback(async () => {
    const fields = exportableFields
      .filter((f) => selectedFields.has(f.fieldname))
      .map((f) => f.fieldname);

    if (fields.length === 0) return;

    // Always include "name" if not already selected
    if (!fields.includes("name")) {
      fields.unshift("name");
    }

    const frappeFilters = mapFiltersToFrappe(doctype, filters ?? []);

    // Compute date range
    const dateRange =
      datePreset === "custom"
        ? { from: customDateFrom || undefined, to: customDateTo || undefined }
        : getDateRange(datePreset);

    const success = await exportList({
      doctype,
      fields,
      filters: frappeFilters,
      fileFormat,
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
      docNames: rowScope === "selected" ? selectedRowNames : undefined,
    });

    if (success) {
      autoCloseRef.current = setTimeout(() => onOpenChange(false), 300);
    }
  }, [
    doctype,
    exportableFields,
    selectedFields,
    filters,
    fileFormat,
    datePreset,
    customDateFrom,
    customDateTo,
    rowScope,
    selectedRowNames,
    exportList,
    onOpenChange,
  ]);

  const selectedCount = selectedFields.size;

  // Segmented control pill (inside a track)
  const segmentClass = (active: boolean) =>
    cn(
      "h-[32px] px-4 rounded-full text-[13px] font-medium transition-all cursor-pointer",
      active
        ? "bg-primary text-white shadow-sm"
        : "text-muted-foreground hover:text-foreground",
    );

  // Standalone date pill (bordered)
  const datePillClass = (active: boolean) =>
    cn(
      "h-[34px] px-4 rounded-full text-[13px] font-medium transition-all cursor-pointer",
      active
        ? "bg-primary text-white shadow-sm"
        : "bg-white dark:bg-gray-900 text-foreground/70 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] flex flex-col p-0 [&>button.absolute]:hidden overflow-hidden rounded-2xl">
        {/* --- Header --- */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800/60 flex-shrink-0">
          <div>
            <DialogTitle className="text-[15px] font-semibold text-foreground">
              Export {doctype}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground/70 mt-0.5">
              {selectedCount} column{selectedCount !== 1 ? "s" : ""} selected
              {rowScope === "selected" && selectedRowNames?.length
                ? ` \u00B7 ${selectedRowNames.length} row${selectedRowNames.length !== 1 ? "s" : ""}`
                : ""}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground/60 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* --- Body: 2-column layout --- */}
        <div className="flex-1 min-h-0 flex">
          {/* Left panel — settings */}
          <div className="w-[280px] shrink-0 border-r border-gray-100 dark:border-gray-800/60 flex flex-col">
            <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
              <h3 className="text-xs font-semibold text-primary uppercase tracking-wide mb-3">
                Settings
              </h3>

              {/* Grouped settings card */}
              <div className="bg-gray-50/80 dark:bg-gray-950/60 rounded-[20px] overflow-hidden">
                {/* Rows */}
                <div className="px-5 py-5">
                  <div className="flex items-center justify-between">
                    <span className="text-[15px] font-medium text-foreground">Rows</span>
                    <div className="flex items-center gap-0.5 bg-gray-200/60 dark:bg-gray-800/60 rounded-full p-0.5">
                      <button type="button" onClick={() => setRowScope("all")} className={segmentClass(rowScope === "all")}>
                        All
                      </button>
                      {hasSelection && (
                        <button type="button" onClick={() => setRowScope("selected")} className={segmentClass(rowScope === "selected")}>
                          {selectedRowNames!.length} selected
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/30" />

                {/* Format */}
                <div className="px-5 py-5">
                  <div className="flex items-center justify-between">
                    <span className="text-[15px] font-medium text-foreground">Format</span>
                    <div className="flex items-center gap-0.5 bg-gray-200/60 dark:bg-gray-800/60 rounded-full p-0.5">
                      <button type="button" onClick={() => setFileFormat("csv")} className={segmentClass(fileFormat === "csv")}>
                        CSV
                      </button>
                      <button type="button" onClick={() => setFileFormat("xlsx")} className={segmentClass(fileFormat === "xlsx")}>
                        Excel
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/30" />

                {/* Date Range */}
                <div className="px-5 py-5">
                  <span className="text-[15px] font-medium text-foreground">Date range</span>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {DATE_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() => setDatePreset(preset.value)}
                        className={datePillClass(datePreset === preset.value)}
                      >
                        {preset.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setDatePreset("custom")}
                      className={datePillClass(datePreset === "custom")}
                    >
                      Custom
                    </button>
                  </div>
                  {datePreset === "custom" && (
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="space-y-1.5">
                        <label className="text-[12px] font-medium text-muted-foreground">From</label>
                        <DatePicker
                          value={customDateFrom}
                          onChange={setCustomDateFrom}
                          placeholder="Start"
                          className="w-full text-[13px]! h-9 border border-gray-200 dark:border-gray-700 rounded-full px-3 bg-white dark:bg-gray-900"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[12px] font-medium text-muted-foreground">To</label>
                        <DatePicker
                          value={customDateTo}
                          onChange={setCustomDateTo}
                          placeholder="End"
                          className="w-full text-[13px]! h-9 border border-gray-200 dark:border-gray-700 rounded-full px-3 bg-white dark:bg-gray-900"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Export button anchored to bottom */}
            <div className="px-5 py-4 flex-shrink-0">
              <Button
                className="w-full rounded-full h-11 text-[14px]"
                onClick={handleExport}
                disabled={noneSelected || isExporting}
              >
                {isExporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export {fileFormat.toUpperCase()}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Right panel — columns */}
          <div className="flex-1 min-w-0 flex flex-col">
            {/* Column header + search */}
            <div className="px-5 pt-4 pb-3 flex-shrink-0 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                  Columns
                  <span className="ml-1.5 text-muted-foreground/40 normal-case tracking-normal font-normal">
                    {selectedCount}/{exportableFields.length}
                  </span>
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowSaveInput((v) => !v)}
                    className="text-[12px] font-medium text-muted-foreground/50 hover:text-foreground transition-colors"
                    title="Save current selection as template"
                  >
                    <Bookmark className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-[12px] font-medium text-[#007AFF] hover:text-[#0051D5] active:text-[#0040B3] transition-colors"
                  >
                    {allSelected ? "Deselect all" : "Select all"}
                  </button>
                </div>
              </div>

              {/* Templates row */}
              {allTemplates.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {allTemplates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => applyTemplate(template)}
                      className="group inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[12px] font-medium bg-gray-100/80 dark:bg-gray-800/60 text-foreground/70 hover:bg-gray-200/80 dark:hover:bg-gray-700/60 hover:text-foreground transition-colors"
                    >
                      {template.isDefault && (
                        <Bookmark className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                      )}
                      <span className="truncate max-w-[120px]">{template.name}</span>
                      <span className="text-muted-foreground/40 text-[11px]">
                        {template.fields.length}
                      </span>
                      {!template.isDefault && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTemplate(template.id);
                          }}
                          className="ml-0.5 opacity-0 group-hover:opacity-100 text-muted-foreground/40 hover:text-red-500 transition-all"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Save template input */}
              {showSaveInput && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={saveTemplateName}
                    onChange={(e) => setSaveTemplateName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSaveTemplate()}
                    placeholder="Template name..."
                    autoFocus
                    className="flex-1 h-8 px-3 text-[13px] rounded-lg border border-gray-200/50 dark:border-gray-700/50 bg-gray-50/60 dark:bg-gray-900/40 text-foreground placeholder:text-muted-foreground/35 focus:outline-none focus:bg-white dark:focus:bg-gray-900 focus:border-gray-300 dark:focus:border-gray-600 transition-all"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-lg text-[12px]"
                    onClick={handleSaveTemplate}
                    disabled={!saveTemplateName.trim() || noneSelected}
                  >
                    Save
                  </Button>
                </div>
              )}

              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/40" />
                <input
                  type="text"
                  value={columnSearch}
                  onChange={(e) => setColumnSearch(e.target.value)}
                  placeholder="Search columns..."
                  className="w-full h-8 pl-8 pr-3 text-[13px] rounded-lg border border-gray-200/50 dark:border-gray-700/50 bg-gray-50/60 dark:bg-gray-900/40 text-foreground placeholder:text-muted-foreground/35 focus:outline-none focus:bg-white dark:focus:bg-gray-900 focus:border-gray-300 dark:focus:border-gray-600 transition-all"
                />
              </div>
            </div>

            {/* Column list */}
            <div className="flex-1 min-h-0">
              <ScrollArea className="h-full">
                <div className="px-2 pb-3">
                  {filteredFields.map((field) => (
                    <label
                      key={field.fieldname}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/40 transition-colors"
                    >
                      <Checkbox
                        checked={selectedFields.has(field.fieldname)}
                        onCheckedChange={() => toggleField(field.fieldname)}
                      />
                      <span className="text-[13px] text-foreground/90 select-none">
                        {field.label}
                      </span>
                    </label>
                  ))}
                  {filteredFields.length === 0 && exportableFields.length > 0 && (
                    <div className="px-3 py-8 text-[13px] text-muted-foreground/50 text-center">
                      No columns match "{columnSearch}"
                    </div>
                  )}
                  {exportableFields.length === 0 && (
                    <div className="px-3 py-8 text-[13px] text-muted-foreground text-center">
                      No columns available
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
